/**
 * DraftDocument - typed renderer for the `draft` payload.
 *
 * Mirrors the server renderer's hierarchy (`render_draft` in the engine's
 * `research_output.py`): the instrument itself comes FIRST and verbatim,
 * because a user copying it into a file must not have to strip commentary out
 * of it. Everything else - parties, governing law, placeholders, assumptions,
 * grounding - is annotation, rendered below a rule.
 *
 * `[BRACKETED]` placeholders in the body are visibly marked so the unfinished
 * parts of a drafted instrument cannot be mistaken for finished text.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCheckLight,
  PiCopySimpleLight,
  PiScrollLight,
  PiWarningCircleLight,
} from 'react-icons/pi';
import { HiChevronDown } from 'react-icons/hi2';
import GlassCard from '../../shared/brand/GlassCard';
import { Well } from '../../shared/brand/Field';
import MarkdownStream from '../../Chat/MarkdownStream';
import { DraftPayload, DraftReview, ResearchCitation, VerificationReport } from '../types';
import { CitationChips, CitationHandlers } from './CitationChip';

/**
 * Same bracket grammar the engine's `review_draft` uses to find undeclared
 * placeholders, so what we highlight is exactly what it audits. Citation
 * markers (`[1]`) are excluded - they are not fill-in fields.
 */
const PLACEHOLDER_RE = /\[[A-Z0-9 _/–-]{3,60}\]/g;

const isCitationMarker = (token: string) => /^\[\d+\]$/.test(token);

/**
 * Wrap every `[BRACKETED]` field in a `<mark>` the markdown renderer keeps
 * (it runs `rehypeRaw`). Returns the body untouched when there is nothing to
 * mark, so a fully-completed draft renders as plain prose.
 */
export function highlightPlaceholders(body: string): string {
  if (!body) return '';
  return body.replace(PLACEHOLDER_RE, (token) =>
    isCitationMarker(token)
      ? token
      : `<mark class="draft-placeholder" data-placeholder="true">${token}</mark>`
  );
}

/** Citation numbers referenced by `sources_relied_on` (a list of `[N]` markers). */
export function sourceNumbers(sourcesReliedOn?: string[]): number[] {
  if (!Array.isArray(sourcesReliedOn)) return [];
  const numbers: number[] = [];
  sourcesReliedOn.forEach((entry) => {
    const matches = String(entry ?? '').match(/\d+/g) || [];
    matches.forEach((n) => {
      const value = Number(n);
      if (Number.isFinite(value) && !numbers.includes(value)) numbers.push(value);
    });
  });
  return numbers;
}

/** Does the review/verification pair carry anything worth surfacing? */
export function hasReviewNotes(review?: DraftReview, verification?: VerificationReport): boolean {
  const reviewCount =
    (review?.issues?.length || 0) + (review?.placeholders_undeclared?.length || 0);
  const verifyCount =
    (verification?.unsupported?.length || 0) + (verification?.dangling_citations?.length || 0);
  return reviewCount + verifyCount > 0;
}

/* ------------------------------------------------------------------ */
/*  Review notes - the same disclosure language as the memo            */
/* ------------------------------------------------------------------ */

const ReviewNotes: React.FC<{
  review?: DraftReview;
  verification?: VerificationReport;
}> = ({ review, verification }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const issues = review?.issues || [];
  const undeclared = review?.placeholders_undeclared || [];
  const unsupported = verification?.unsupported || [];
  const dangling = verification?.dangling_citations || [];
  const total = issues.length + undeclared.length + unsupported.length + dangling.length;
  if (total === 0) return null;

  return (
    <div className="glass rounded-lg shadow-sm overflow-hidden" data-testid="draft-review">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-start
                   transition-colors duration-200 ease-slide hover:bg-surface-sunken/60"
      >
        <HiChevronDown
          className={`w-3.5 h-3.5 text-fg-muted transition-transform duration-300 ease-slide ${
            open ? 'rotate-0' : '-rotate-90'
          }`}
        />
        <span className="flex-1 text-[13px] font-semibold text-fg-strong">
          {t('Review notes')}
        </span>
        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium
                         bg-surface-sunken text-fg-muted border border-hairline-soft">
          {total}
        </span>
      </button>

      {open && (
        <div className="px-4 pb-3 pt-1 space-y-3 text-[13px] text-fg-body reveal-item">
          {/* These are checks, not verdicts - the engine annotates, never rewrites. */}
          <p className="text-[11px] text-fg-muted">
            {t('Automated checks on the draft. Review before use.')}
          </p>

          {issues.length > 0 && (
            <ul className="space-y-1" data-testid="draft-review-issues">
              {issues.map((issue, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-champagne flex-shrink-0" aria-hidden />
                  <span className="leading-relaxed">{issue}</span>
                </li>
              ))}
            </ul>
          )}

          {undeclared.length > 0 && (
            <div>
              <p className="text-[11px] font-medium text-fg-muted mb-1">
                {t('Fields in the document not listed as placeholders')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {undeclared.map((field) => (
                  <code
                    key={field}
                    className="px-1.5 py-0.5 rounded text-[11px] bg-surface-sunken
                               border border-hairline-soft text-fg-body"
                  >
                    {field}
                  </code>
                ))}
              </div>
            </div>
          )}

          {unsupported.length > 0 && (
            <div>
              <p className="text-[11px] font-medium text-fg-muted mb-1">
                {t('Claims without a supporting citation')}
              </p>
              <ul className="space-y-1">
                {unsupported.map((entry, i) => (
                  <li key={i} className="leading-relaxed">
                    {entry.claim}
                    {entry.why && <span className="text-fg-muted"> - {entry.why}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {dangling.length > 0 && (
            <p className="text-xs text-fg-muted">
              {t('Citation markers with no matching source')}: {dangling.join(', ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main renderer                                                      */
/* ------------------------------------------------------------------ */

interface Props extends CitationHandlers {
  payload: DraftPayload;
  citations: ResearchCitation[];
  /** R6 self-review, lifted off the envelope meta by the dispatcher. */
  review?: DraftReview;
  /** R5 groundedness annotation. */
  verification?: VerificationReport;
  /** The server-rendered markdown - what "copy" puts on the clipboard. */
  markdown?: string;
}

const DraftDocument: React.FC<Props> = ({
  payload,
  citations,
  review,
  verification,
  markdown,
  ...handlers
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const body = payload.body_markdown || '';
  const markedBody = useMemo(() => highlightPlaceholders(body), [body]);
  const parties = Array.isArray(payload.parties) ? payload.parties : [];
  const placeholders = Array.isArray(payload.placeholders) ? payload.placeholders : [];
  const assumptions = Array.isArray(payload.assumptions) ? payload.assumptions : [];
  const grounding = sourceNumbers(payload.sources_relied_on);

  const handleCopy = () => {
    // Copy the instrument, not the annotations - that is what gets pasted
    // into a document. `markdown` (render_draft output) is the fallback.
    const text = body || markdown || '';
    if (!text) return;
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="font-sans space-y-3 reveal-item" data-testid="draft-document">
      {/* ── The instrument ──────────────────────────────────── */}
      <GlassCard
        padding="lg"
        icon={<PiScrollLight />}
        title={payload.document_type || t('Draft document')}
        subtitle={payload.language ? `${t('Language')}: ${payload.language}` : undefined}
        actions={
          <button
            type="button"
            onClick={handleCopy}
            data-testid="draft-copy"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium
                       text-fg-muted hover:text-champagne transition-colors duration-200 ease-slide"
          >
            {copied ? (
              <PiCheckLight className="w-3.5 h-3.5 text-champagne" />
            ) : (
              <PiCopySimpleLight className="w-3.5 h-3.5" />
            )}
            {copied ? t('Copied') : t('Copy document')}
          </button>
        }
      >
        {payload.title && (
          <h3 className="text-base font-semibold text-fg-strong mb-3" data-testid="draft-title">
            {payload.title}
          </h3>
        )}

        {/* The body renders through the shared markdown path, so clauses,
            headings and inline [N] citations behave as everywhere else. */}
        <MarkdownStream
          text={markedBody}
          citations={citations}
          isLive={false}
          onComplete={() => {}}
          onOpenCitation={handlers.onOpenCitation}
        />
      </GlassCard>

      {/* ── Annotations, below the rule (as render_draft does) ── */}
      {(parties.length > 0 || payload.governing_law) && (
        <div className="grid gap-2 sm:grid-cols-2">
          {parties.length > 0 && (
            <Well data-testid="draft-parties">
              <p className="text-[11px] font-medium text-fg-muted mb-1">{t('Parties')}</p>
              <ul className="space-y-0.5">
                {parties.map((party, i) => (
                  <li key={i} className="text-sm text-fg-body">
                    {party.role && <span className="text-fg-muted">{party.role}: </span>}
                    {party.name || <span className="text-fg-muted">{t('Not set')}</span>}
                  </li>
                ))}
              </ul>
            </Well>
          )}
          {payload.governing_law && (
            <Well data-testid="draft-governing-law">
              <p className="text-[11px] font-medium text-fg-muted mb-1">{t('Governing law')}</p>
              <p className="text-sm text-fg-body">{payload.governing_law}</p>
            </Well>
          )}
        </div>
      )}

      {placeholders.length > 0 && (
        <Well>
          <p className="text-[11px] font-medium text-fg-muted mb-1.5 flex items-center gap-1.5">
            <PiWarningCircleLight className="w-3.5 h-3.5" aria-hidden />
            {t('Complete before use')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {placeholders.map((field, i) => (
              <code
                key={`${field}-${i}`}
                data-testid="draft-placeholder"
                className="px-1.5 py-0.5 rounded text-[11px] font-medium
                           bg-champagne/15 border border-champagne/40 text-fg-strong"
              >
                {field}
              </code>
            ))}
          </div>
        </Well>
      )}

      {assumptions.length > 0 && (
        <Well data-testid="draft-assumptions">
          <p className="text-[11px] font-medium text-fg-muted mb-1">{t('Assumptions made')}</p>
          <ul className="space-y-1">
            {assumptions.map((assumption, i) => (
              <li key={i} className="text-xs text-fg-muted leading-relaxed">
                {assumption}
              </li>
            ))}
          </ul>
        </Well>
      )}

      {grounding.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="draft-grounding">
          <span className="text-[11px] font-medium text-fg-muted">{t('Grounded in')}</span>
          <CitationChips numbers={grounding} citations={citations} {...handlers} />
        </div>
      )}

      <ReviewNotes review={review} verification={verification} />
    </div>
  );
};

export default DraftDocument;
